import React from "react";
import { motion } from "framer-motion";
import { FileText, ClipboardList, Users, ArrowUpRight } from "lucide-react";
import { CALENDLY_URL } from "@/lib/site-data";

const SERVICES = [
  {
    id: "grant-writing",
    number: "01",
    icon: FileText,
    title: "Grant Writing & Fundraising Strategy",
    blurb:
      "From prospecting to polished proposals, we position your mission for sustainable resources.",
    bullets: [
      "Funder research & fit assessment",
      "Proposal drafting & editing",
      "Budgets and logic models",
    ],
    accent: "bg-brand-blue text-brand-cream",
  },
  {
    id: "project-management",
    number: "02",
    icon: ClipboardList,
    title: "Project Management & Program Development",
    blurb:
      "Hands-on management of the projects that matter most to your community.",
    bullets: [
      "Group mentoring programs",
      "Community wellness circles",
      "Invoice collection & operations",
    ],
    accent: "bg-brand-periwinkle text-brand-ink",
  },
  {
    id: "staffing",
    number: "03",
    icon: Users,
    title: "Staffing & Facilitation Support",
    blurb:
      "Skilled facilitation, hosting, and execution for the moments that bring your mission to life.",
    bullets: [
      "Event hosting & panel moderation",
      "Virtual & in-person facilitation",
      "Wellness workshop ideation & delivery",
    ],
    accent: "bg-brand-ink text-brand-cream",
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <p className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-brand-sage mb-5">
              What we do
            </p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-brand-ink">
              Three pillars of{" "}
              <span className="italic text-brand-blue">implementation-focused</span>{" "}
              support.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-base md:text-lg text-brand-ink/75 leading-relaxed">
              Built for mission-driven organizations, small businesses, and
              community-rooted leaders who need a partner — not a polished
              powerpoint.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.12 }}
                data-testid={`service-card-${s.id}`}
                className="group relative bg-white/70 backdrop-blur-sm border border-brand-blue/15 rounded-3xl p-8 md:p-10 hover:border-brand-blue/40 hover:bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-brand-blue/10 flex flex-col h-full"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${s.accent} group-hover:scale-110 transition-transform duration-500`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <span className="font-serif text-2xl text-brand-blue/30 group-hover:text-brand-blue/60 transition-colors">
                    {s.number}
                  </span>
                </div>

                <h3 className="font-serif text-xl md:text-2xl leading-tight text-brand-ink mb-3 group-hover:text-brand-blue transition-colors">
                  {s.title}
                </h3>
                <p className="text-brand-ink/70 text-[15px] leading-relaxed mb-6 font-sans">
                  {s.blurb}
                </p>

                <ul className="space-y-2 mb-8 flex-1">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-brand-ink/80 font-sans">
                      <span className="mt-2 w-1 h-1 rounded-full bg-brand-blue flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-cta-${s.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-blue group-hover:gap-3 transition-all"
                >
                  Let's talk
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-12" />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
